import { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Segmented, Spin, Table, Tabs, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
  ChevronLeft,
  ChevronRight,
  DollarSign,
  TrendingUp,
} from "lucide-react";
import { fetchReport, ReportTransaction, StaffPerformance } from "../../store/reportsSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import axiosClient from "../../api/axiosClient";
import ReportsKpiTab from "./ReportsKpiTab";
import ReportsScheduledTab from "./ReportsScheduledTab";
import "./AdminReportsPage.css";

dayjs.extend(isoWeek);

type Period = "day" | "week" | "month" | "custom";

function getRange(period: Period, anchor: Dayjs, custom: [Dayjs, Dayjs] | null): [Dayjs, Dayjs] {
  if (period === "custom" && custom) return custom;
  return [anchor.startOf(period as any), anchor.endOf(period as any)];
}

function getPeriodLabel(period: Period, anchor: Dayjs, custom: [Dayjs, Dayjs] | null): string {
  if (period === "day")   return anchor.format("dddd, MMMM D, YYYY");
  if (period === "week")  return `${anchor.startOf("isoWeek").format("MMM D")} – ${anchor.endOf("isoWeek").format("MMM D, YYYY")}`;
  if (period === "month") return anchor.format("MMMM YYYY");
  if (period === "custom" && custom) {
    return `${custom[0].format("MMM D")} – ${custom[1].format("MMM D, YYYY")}`;
  }
  return "Custom Range";
}

export default function AdminReportsPage() {
  const dispatch = useAppDispatch();
  const { data, loading } = useAppSelector((s) => s.reports);
  const auth = useAppSelector((s) => s.auth);
  const isOwner = auth.role === "admin";

  const [businessId, setBusinessId] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>("day");
  const [anchor, setAnchor] = useState<Dayjs>(dayjs());
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [pendingCustom, setPendingCustom] = useState<[Dayjs, Dayjs] | null>(null);

  useEffect(() => {
    const token = window.localStorage.getItem("accessToken");
    if (!token) return;
    axiosClient
      .get("/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const d = res.data as { business_id?: string };
        if (d.business_id) setBusinessId(d.business_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!businessId) return;
    if (period === "custom" && !customRange) return;
    const [from, to] = getRange(period, anchor, customRange);
    dispatch(fetchReport({
      businessId,
      from: from.toISOString(),
      to: to.add(1, "second").toISOString(),
    }));
  }, [businessId, period, anchor, customRange, dispatch]);

  function step(delta: number) {
    if (period === "day")   setAnchor((d) => d.add(delta, "day"));
    if (period === "week")  setAnchor((d) => d.add(delta, "week"));
    if (period === "month") setAnchor((d) => d.add(delta, "month"));
  }

  function applyCustom() {
    if (pendingCustom) setCustomRange(pendingCustom);
  }

  function applyPreset(range: [Dayjs, Dayjs]) {
    setPeriod("custom");
    setCustomRange(range);
    setPendingCustom(range);
  }

  const PRESETS: { label: string; range: () => [Dayjs, Dayjs] }[] = [
    { label: "Yesterday", range: () => [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
    { label: "Last Week", range: () => [dayjs().subtract(1, "week").startOf("isoWeek"), dayjs().subtract(1, "week").endOf("isoWeek")] },
    { label: "Last Month", range: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
    { label: "Last Year", range: () => [dayjs().subtract(1, "year").startOf("year"), dayjs().subtract(1, "year").endOf("year")] },
  ];

  // Derived summary — non-owners only ever see their own earnings, never the full business report
  const transactions = useMemo(() => {
    const all = data?.transactions ?? [];
    return isOwner ? all : all.filter((t) => t.staff_id === auth.userId);
  }, [data, isOwner, auth.userId]);
  const totalEarnings = transactions.reduce((s, t) => s + t.price, 0);
  const txCount = transactions.length;
  const avgTx = txCount > 0 ? totalEarnings / txCount : 0;

  // Transaction table columns — Staff column only makes sense for the owner's business-wide view
  const txColumns: ColumnsType<ReportTransaction> = [
    {
      title: "Time",
      dataIndex: "start_time",
      key: "time",
      render: (v) => dayjs(v).format("h:mm A"),
      width: 90,
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client",
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      render: (v) => <span style={{ color: "#6b7280" }}>{v}</span>,
    },
    ...(isOwner
      ? [{
          title: "Staff",
          dataIndex: "staff_name",
          key: "staff",
        } as ColumnsType<ReportTransaction>[number]]
      : []),
    {
      title: "Amount",
      dataIndex: "price",
      key: "price",
      align: "right",
      render: (v) => <span style={{ fontWeight: 600, color: "#16a34a" }}>${v.toFixed(2)}</span>,
    },
  ];

  // Staff performance columns
  const staffColumns: ColumnsType<StaffPerformance> = [
    {
      title: "Rank",
      key: "rank",
      width: 70,
      render: (_, __, i) => (
        <span style={{ fontWeight: 600 }}>{i === 0 ? "🏆" : `#${i + 1}`}</span>
      ),
    },
    {
      title: "Staff Member",
      dataIndex: "staff_name",
      key: "name",
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: "Appointments",
      dataIndex: "total_appointments",
      key: "appts",
      align: "right",
      render: (v) => v.toLocaleString(),
    },
    {
      title: "Total Earnings",
      dataIndex: "total_earnings",
      key: "earnings",
      align: "right",
      render: (v) => <span style={{ fontWeight: 600, color: "#16a34a" }}>${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>,
    },
    {
      title: "Avg / Service",
      dataIndex: "avg_per_service",
      key: "avg",
      align: "right",
      render: (v) => <span style={{ color: "#6b7280" }}>${v.toFixed(2)}</span>,
    },
  ];

  const busiest = data?.busiest_periods;
  const staffPerf = data?.staff_performance ?? [];
  const topStaff = staffPerf[0];

  const dashboardContent = (
    <div className="admin-reports-page">
      {/* ── Controls Bar ── */}
      <div className="reports-controls-bar">
        <div className="reports-controls-top">
          <h1>{isOwner ? "Business Reports" : "My Earnings"}</h1>
          {!isOwner && <span style={{ fontSize: 13, color: "#6b7280" }}>Viewing: my earnings only</span>}
          <Segmented
            value={period}
            onChange={(v) => { setPeriod(v as Period); setCustomRange(null); }}
            options={[
              { label: "Day",    value: "day" },
              { label: "Week",   value: "week" },
              { label: "Month",  value: "month" },
              { label: "Custom", value: "custom" },
            ]}
          />
        </div>

        <div className="reports-controls-bottom">
          {period !== "custom" ? (
            <>
              <div className="reports-date-nav">
                <button className="reports-date-nav-btn" onClick={() => step(-1)}>
                  <ChevronLeft size={18} />
                </button>
                <button className="reports-date-nav-btn" onClick={() => step(1)}>
                  <ChevronRight size={18} />
                </button>
              </div>
              <span className="reports-date-label">
                {getPeriodLabel(period, anchor, null)}
              </span>
              <Button size="small" onClick={() => setAnchor(dayjs())}>Today</Button>
            </>
          ) : (
            <>
              <DatePicker.RangePicker
                onChange={(dates) => {
                  if (dates?.[0] && dates?.[1]) {
                    setPendingCustom([dates[0], dates[1]]);
                  }
                }}
              />
              <Button
                type="primary"
                disabled={!pendingCustom}
                onClick={applyCustom}
                style={{ background: "#2563eb", borderColor: "#2563eb" }}
              >
                Apply
              </Button>
              {customRange && (
                <span className="reports-date-label" style={{ minWidth: "auto" }}>
                  {getPeriodLabel("custom", anchor, customRange)}
                </span>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                {PRESETS.map((p) => (
                  <Button key={p.label} size="small" onClick={() => applyPreset(p.range())}>
                    {p.label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <Spin spinning={loading}>
        <div className="reports-content">

          {/* Summary Cards */}
          <div className="reports-summary-grid">
            <div className="reports-stat-card green">
              <div className="reports-stat-header">
                <span className="reports-stat-label">Total Earnings</span>
                <DollarSign size={18} color="#22c55e" />
              </div>
              <div className="reports-stat-value">${totalEarnings.toFixed(2)}</div>
              <div className="reports-stat-sub">from completed bookings</div>
            </div>

            <div className="reports-stat-card blue">
              <div className="reports-stat-header">
                <span className="reports-stat-label">Transactions</span>
                <TrendingUp size={18} color="#3b82f6" />
              </div>
              <div className="reports-stat-value">{txCount}</div>
              <div className="reports-stat-sub">completed appointments</div>
            </div>

            <div className="reports-stat-card purple">
              <div className="reports-stat-header">
                <span className="reports-stat-label">Average Transaction</span>
                <DollarSign size={18} color="#a855f7" />
              </div>
              <div className="reports-stat-value">${avgTx.toFixed(2)}</div>
              <div className="reports-stat-sub">per completed booking</div>
            </div>
          </div>

          {/* Busiest Periods */}
          {busiest && (
            <div className="reports-busiest-grid">
              {([
                { key: "day",   color: "blue",   title: "Busiest Day (All Time)" },
                { key: "week",  color: "purple", title: "Busiest Week (All Time)" },
                { key: "month", color: "orange", title: "Busiest Month (All Time)" },
                { key: "year",  color: "green",  title: "Busiest Year (All Time)" },
              ] as const).map(({ key, color, title }) => {
                const p = busiest[key];
                return (
                  <div key={key} className={`reports-busiest-card ${color}`}>
                    <div className="reports-busiest-title">{title}</div>
                    <div className="reports-busiest-label">{p.label || "—"}</div>
                    <div className="reports-busiest-meta">
                      {p.count > 0 ? (
                        <>{p.count} appointments<br />${p.earnings.toFixed(2)} earned</>
                      ) : (
                        "No data yet"
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Staff Performance — owner-only, since it compares everyone's numbers */}
          {isOwner && staffPerf.length > 0 && (
            <div className="reports-panel">
              <div className="reports-panel-header">
                <h2>All-Time Staff Performance</h2>
              </div>
              <Table
                dataSource={staffPerf}
                columns={staffColumns}
                rowKey="staff_id"
                pagination={false}
                rowClassName={(_, i) => i === 0 ? "ant-table-row-selected" : ""}
                size="middle"
              />
              {topStaff && (
                <div className="reports-top-staff-banner">
                  <span className="reports-top-staff-trophy">🏆</span>
                  <div>
                    <div className="reports-top-staff-title">Highest Earning Staff Member (All Time)</div>
                    <div className="reports-top-staff-sub">
                      {topStaff.staff_name} — ${topStaff.total_earnings.toLocaleString(undefined, { minimumFractionDigits: 2 })} from {topStaff.total_appointments.toLocaleString()} appointments
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Transactions Table */}
          <div className="reports-panel">
            <div className="reports-panel-header">
              <h2>Detailed Earnings</h2>
            </div>
            <Table
              dataSource={transactions}
              columns={txColumns}
              rowKey="booking_id"
              pagination={{ pageSize: 25, showSizeChanger: false }}
              size="middle"
              locale={{ emptyText: "No completed transactions in this period" }}
              summary={() => txCount > 0 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <span style={{ fontWeight: 600, color: "#111827" }}>Total</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <span style={{ fontWeight: 700, color: "#16a34a" }}>${totalEarnings.toFixed(2)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null}
            />
          </div>

        </div>
      </Spin>
    </div>
  );

  if (!isOwner) return dashboardContent;

  return (
    <div style={{ padding: "0 0 24px" }}>
      <Tabs
        style={{ padding: "0 24px" }}
        items={[
          { key: "dashboard", label: "Dashboard", children: dashboardContent },
          { key: "kpis", label: "Custom KPIs", children: <div style={{ padding: "16px 24px" }}><ReportsKpiTab /></div> },
          { key: "scheduled", label: "Scheduled Reports", children: <div style={{ padding: "16px 24px" }}><ReportsScheduledTab /></div> },
        ]}
      />
    </div>
  );
}
